import axios from "axios";
import { useContext } from 'react';
import { GlobalStateContext } from '../wrappers/GlobalContext';

const useApiRequest = () => {
    const { apimToken, setApimToken, apimExpTime, setApimExpTime } = useContext(GlobalStateContext);
    const { currentUser } = useContext(GlobalStateContext);

    const assembleBaseUrl = () => {
        let baseUrl = `${process.env.REACT_APP_API_HOST}/api/banco_preco/`;
        if (process.env.REACT_APP_API_PORT && process.env.REACT_APP_API_PORT.length > 0) {
            baseUrl = `${process.env.REACT_APP_API_HOST}:${process.env.REACT_APP_API_PORT}/api/banco_preco/`;
        }
        return baseUrl;
    };

    const baseUrl = assembleBaseUrl();

    const queryStringConverter = (params) => {
        if (!params) return '';

        const queryString = Object.keys(params)
            .filter(key => {
                const value = params[key];
                return value != null && (!Array.isArray(value) || value.length > 0);
            })
            .map(key => {
                if (Array.isArray(params[key])) {
                    return params[key].map(value => `${key}=${encodeURIComponent(value)}`).join('&');
                }
                return `${key}=${encodeURIComponent(params[key])}`;
            })
            .join('&');

        return queryString ? '?' + queryString : '';
    };

    const get = async (endpoint, params, token) => {
        try {
            const data = await axios.get(
                `${baseUrl}${endpoint}${queryStringConverter(params)}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token || apimToken || ''}`
                    }
                }
            );
            return data.data;
        } catch (e) {
            throw e;
        }
    };

    const post = async (endpoint, data, config = {}) => {
        try {
            const headers = {
                'Authorization': `Bearer ${apimToken || ''}`,
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            };
            const response = await axios.post(
                `${baseUrl}${endpoint}`,
                data,
                { headers }
            );
            return response.data;
        } catch (e) {
            throw e;
        }
    };

    const put = async (endpoint, data, config = {}) => {
        try {
            const response = await axios.put(
                `${baseUrl}${endpoint}`,
                data,
                {
                    headers: {
                        'Authorization': `Bearer ${apimToken || ''}`
                    },
                    ...config
                }
            );
            return response.data;
        } catch (e) {
            throw e;
        }
    };

    const remove = async (endpoint) => {
        try {
            await axios.delete(
                `${baseUrl}${endpoint}`,
                {
                    headers: {
                        'Authorization': `Bearer ${apimToken || ''}`
                    },
                },
            );
        } catch (e) {
            throw e;
        }
    };

    const downloadFile = async (endpoint, params, fileName) => {
        try {
            await axios({
                url: `${baseUrl}${endpoint}${queryStringConverter(params)}`,
                method: 'GET',
                responseType: 'blob',
                headers: {
                    'Authorization': `Bearer ${apimToken || ''}`
                }
            }).then((response) => {
                const href = URL.createObjectURL(response.data);

                const link = document.createElement('a');
                link.href = href;
                link.setAttribute('download', fileName);
                document.body.appendChild(link);
                link.click();

                document.body.removeChild(link);
                URL.revokeObjectURL(href);
            });

        } catch (e) {
            throw e;
        }

    };

    const getApimToken = async () => {
        try {
            const consumerKey = process.env.REACT_APP_APIM_CONSUMER_KEY;
            const consumerSecret = process.env.REACT_APP_APIM_CONSUMER_SECRET;

            const basic64 = window.btoa(`${consumerKey}:${consumerSecret}`).toString();

            const encodedParams = new URLSearchParams();
            encodedParams.set('grant_type', 'client_credentials');
            encodedParams.set('scope', `device_${window.btoa(currentUser?.profile.username).toString()}`);

            const options = {
                method: 'POST',
                url: process.env.REACT_APP_APIM_TOKEN_URL,
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    Authorization: `Basic ${basic64}`,
                },
                data: encodedParams,
            };

            const response = await axios.request(options);
            return response.data;
        } catch (err) {
            return { error: true, message: err.message };
        }
    };

    return {
        queryStringConverter,
        post,
        put,
        get,
        remove,
        downloadFile,
        getApimToken,
        baseUrl
    };
};

export default useApiRequest;
